/**
 * Deep merge utility for combining translation objects
 *
 * Features:
 * - Recursively merges nested objects
 * - Arrays are replaced (not merged)
 * - Null values in override DELETE the base key
 * - Preserves type safety
 *
 * @param base - Base translation object
 * @param override - Override translation object
 * @returns Merged translation object
 *
 * @example
 * const base = { a: 1, b: { c: 2, d: 3 } };
 * const override = { b: { d: 4 }, e: 5 };
 * deepMerge(base, override);
 * // Result: { a: 1, b: { c: 2, d: 4 }, e: 5 }
 *
 * @example Deleting keys with null
 * const base = { settings: { production: "Production" } };
 * const override = { settings: { production: null } };
 * deepMerge(base, override);
 * // Result: { settings: {} } // "production" key removed
 */
export function deepMerge<T extends Record<string, any>>(
  base: T,
  override: Partial<T>
): T {
  const result = { ...base };

  for (const key in override) {
    if (Object.prototype.hasOwnProperty.call(override, key)) {
      const overrideValue = override[key];
      const baseValue = result[key];

      // Null override = DELETE base key
      if (overrideValue === null) {
        delete result[key];
        continue;
      }

      // Undefined = skip (don't override)
      if (overrideValue === undefined) {
        continue;
      }

      // Both are objects = recursive merge
      if (isPlainObject(baseValue) && isPlainObject(overrideValue)) {
        result[key] = deepMerge(baseValue, overrideValue);
      } else {
        // Primitive or array = replace
        result[key] = overrideValue as any;
      }
    }
  }

  return result;
}

/**
 * Check if value is a plain object (not array, date, etc.)
 */
function isPlainObject(value: any): value is Record<string, any> {
  return (
    value !== null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    !(value instanceof Date) &&
    !(value instanceof RegExp)
  );
}
